import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Star, 
  MapPin, 
  Wifi, 
  Coffee, 
  Car, 
  Dumbbell, 
  Waves,
  Sparkles,
  Utensils,
  Tv,
  Wind,
  Heart,
  Share2,
  Calendar,
  Users,
  ChevronRight,
  ExternalLink,
  Loader,
  AlertCircle,
  Plane,
  Hotel,
  Building,
  ShieldAlert,
  ImageIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import HotelService from './HotelService';
import UnsplashService from './UnsplashService';
import { AuthContext } from '../../../../Context/AuthContext';

// Format dates to DD/MM/YYYY for Booking.com
const formatDateForBooking = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Simplify location to just city/country name
const simplifyLocation = (locationStr) => {
  if (!locationStr || typeof locationStr !== 'string') return '';
  return locationStr.split('(')[0].trim();
};

// Booking.com URL construction for hotels with guest and room info
const constructBookingUrl = (hotel, location, checkInDate, checkOutDate, guests = { adults: 2, children: 0, rooms: 1 }) => {
  try {
    const cityName = simplifyLocation(location || hotel?.location || '');
    const hotelName = hotel?.name ? encodeURIComponent(hotel.name) : '';
    const encodedCity = encodeURIComponent(cityName);
    
    const checkIn = formatDateForBooking(checkInDate);
    const checkOut = formatDateForBooking(checkOutDate);

    // Base URL with city search
    let baseUrl = `https://www.booking.com/searchresults.html?ss=${encodedCity}`;
    
    // Add hotel name if available
    if (hotelName) {
      baseUrl += `+${hotelName}`;
    }

    // Add dates if available
    if (checkIn && checkOut) {
      baseUrl += `&checkin_date=${checkIn}&checkout_date=${checkOut}`;
    }

    // Add guest and room info with actual values
    const adults = guests?.adults || 2;
    const children = guests?.children || 0;
    const rooms = guests?.rooms || 1;
    
    baseUrl += `&group_adults=${adults}&group_children=${children}&no_rooms=${rooms}`;

    return baseUrl;
  } catch (error) {
    console.error('Error constructing Booking.com URL:', error);
    return 'https://www.booking.com/hotels';
  }
};

const handleBookOnBooking = (hotel, location, checkInDate, checkOutDate, guests) => {
  const bookingUrl = constructBookingUrl(hotel, location, checkInDate, checkOutDate, guests);
  window.open(bookingUrl, '_blank', 'noopener,noreferrer');
};

// Enhanced HotelCard with full originalData rendering
const HotelCard = ({ hotel, location, checkInDate, checkOutDate, onBook }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Validate hotel data
  const isValidHotel = hotel && (hotel.name || hotel.originalData?.name);
  
  // Extract all data from originalData with proper fallback
  const nestedOriginal = hotel?.originalData?.originalData || {};
  const hotelName = hotel?.name || hotel?.originalData?.name || nestedOriginal?.hotelId || 'Unknown Hotel';
  const hotelPrice = hotel?.price || hotel?.originalData?.price || 'N/A';
  const hotelRating = hotel?.rating || hotel?.originalData?.rating || nestedOriginal?.rating || '0';
  const hotelLocation = hotel?.location || hotel?.originalData?.location || 'Location not specified';
  const hotelAmenities = hotel?.amenities || hotel?.originalData?.amenities || [];
  const hotelImage = hotel?.image || hotel?.originalData?.image;
  
  // Extract all originalData fields
  const provider = hotel?.originalData?.provider || '';
  const hotelId = nestedOriginal?.hotelId || '';
  const chainCode = nestedOriginal?.chainCode || '';
  const iataCode = nestedOriginal?.iataCode || '';
  const latitude = nestedOriginal?.latitude || null;
  const longitude = nestedOriginal?.longitude || null;
  const distance = nestedOriginal?.distance ? 
    `${nestedOriginal.distance}${nestedOriginal.distanceUnit || 'KM'} from center` : '';

  const getAmenityIcon = (amenity) => {
    const amenityKey = Object.keys(icons).find(key => 
      amenity.toLowerCase().includes(key.toLowerCase())
    ) || amenity;
    return icons[amenityKey] || <Wifi size={14} />;
  };

  const icons = {
    'WiFi': <Wifi size={14} />,
    'Breakfast': <Coffee size={14} />,
    'Parking': <Car size={14} />,
    'Gym': <Dumbbell size={14} />,
    'Pool': <Waves size={14} />,
    'Spa': <Sparkles size={14} />,
    'Beach Access': <Waves size={14} />,
    'Restaurant': <Utensils size={14} />,
    'Bar': <Utensils size={14} />,
    'Concierge': <Users size={14} />,
    'Fireplace': <Wind size={14} />,
    'Hiking': <Sparkles size={14} />,
    'Business Center': <Tv size={14} />,
    'Room Service': <Utensils size={14} />,
    'Air Conditioning': <Wind size={14} />,
  };

  // Don't render invalid hotel cards
  if (!isValidHotel) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Hotel Image */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {hotelImage && !imageError ? (
          <img 
            src={hotelImage} 
            alt={hotelName}
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
            <Building size={48} className="text-blue-300" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        
        {/* Favorite Button */}
        <button
          onClick={() => setIsFavorite(!isFavorite)}
          className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
        >
          <Heart 
            size={18} 
            className={isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'} 
          />
        </button>

        {/* Provider api Badge  (Amades name) */}
        {/* {provider && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg">
            <span className="text-xs font-semibold text-gray-700">{provider}</span>
          </div>
        )} */}

        {/* Rating Badge */}
        {hotelRating !== '0' && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg">
            <Star size={14} className="text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-bold text-gray-900">{Number(hotelRating)?.toFixed(1) || 'N/A'}</span>
          </div>
        )}
      </div>

      {/* Hotel Info */}
      <div className="p-4 md:p-6">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">{hotelName}</h3>
            <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
              <MapPin size={14} />
              <span className="truncate">{hotelLocation}</span>
            </div>
          </div>
        </div>

        {/* Distance from center */}
        {distance && (
          <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
            <Building size={14} />
            <span>{distance}</span>
          </div>
        )}

        {/* Hotel ID and Chain Info */}
        {(hotelId || chainCode || iataCode) && (
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-400">
            {hotelId && <span>ID: {hotelId}</span>}
            {chainCode && <span>• Chain: {chainCode}</span>}
            {iataCode && <span>• Airport: {iataCode}</span>}
          </div>
        )}

        {/* Coordinates */}
        {latitude && longitude && (
          <div className="mt-2 text-xs text-gray-400">
            📍 {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </div>
        )}

        {/* Amenities - Show ALL from originalData */}
        {hotelAmenities.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {hotelAmenities.map((amenity, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                {getAmenityIcon(amenity)}
                {amenity}
              </span>
            ))}
          </div>
        )}

        {/* Price & Book Button */}
        <div className="flex items-end justify-between mt-4 pt-4 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">Price per night</span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-blue-600">
                {hotelPrice}
              </span>
            </div>
          </div>

          <button
            onClick={() => onBook(hotel)}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-semibold hover:opacity-90 transition-all flex items-center gap-2"
          >
            <ExternalLink size={16} />
            Book on Booking.com
          </button>
        </div>
      </div>
    </div>
  );
};

const HotelList = () => {
  const { frondId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const hotelService = HotelService;

  // Get search params from location state
  const searchParams = location.state?.searchParams || {};
  const defaultLocation = searchParams.location || '';
  const defaultCheckIn = searchParams.checkInDate || '';
  const defaultCheckOut = searchParams.checkOutDate || '';

  // State
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchLocation, setSearchLocation] = useState(defaultLocation);
  const [checkInDate, setCheckInDate] = useState(defaultCheckIn || new Date().toISOString().split('T')[0]);
  const [checkOutDate, setCheckOutDate] = useState(defaultCheckOut || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [guests, setGuests] = useState({ adults: 5, children: 0, rooms: 2 });
  const [searched, setSearched] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);

  // Set token for API calls
  useEffect(() => {
    if (token) {
      hotelService.setToken(token);
    }
  }, [token]);

  // Process API response data
  const processHotelData = (apiData) => {
    try {
      if (!apiData || !Array.isArray(apiData)) {
        return [];
      }

      return apiData
        .map((item, index) => {
          // Get the nested originalData - could be at item.originalData or item.originalData.originalData
          const nestedOriginal = item.originalData?.originalData || item.originalData;
          
          // Priority: top-level fields > originalData > nested originalData
          const data = {
            id: item.id || item.originalData?.id || nestedOriginal?.hotelId || `hotel-${index}-${Date.now()}`,
            name: item.name || item.originalData?.name || nestedOriginal?.hotelId || 'Unknown Hotel',
            price: item.price || item.originalData?.price || 'N/A',
            rating: item.rating || item.originalData?.rating || nestedOriginal?.rating || '0',
            location: item.location || item.originalData?.location || 'Location not specified',
            amenities: item.amenities || item.originalData?.amenities || [],
            image: item.image || item.originalData?.image || null,
            link: item.link || item.originalData?.link || '',
            distance: nestedOriginal?.distance ? 
              `${nestedOriginal.distance}${nestedOriginal.distanceUnit || 'KM'} from center` : '',
            provider: item.originalData?.provider || '',
            hotelId: nestedOriginal?.hotelId || '',
            chainCode: nestedOriginal?.chainCode || '',
            iataCode: nestedOriginal?.iataCode || '',
            latitude: nestedOriginal?.latitude || null,
            longitude: nestedOriginal?.longitude || null,
            // Keep originalData reference for any additional data
            originalData: item.originalData
          };
          
          return data;
        })
        .filter(hotel => hotel !== null && hotel.name); // Remove null/empty entries
    } catch (error) {
      console.error('Error processing hotel data:', error);
      return [];
    }
  };

  const handleSearchHotels = async () => {
    if (!searchLocation.trim()) {
      toast.error('Please enter a destination');
      return;
    }

    if (!checkInDate || !checkOutDate) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      // Format dates to DD-MM-YYYY for API
      const formatDateDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      };

      const formattedCheckIn = formatDateDDMMYYYY(checkInDate);
      const formattedCheckOut = formatDateDDMMYYYY(checkOutDate);
      
      if (!formattedCheckIn || !formattedCheckOut) {
        throw new Error('Invalid date format');
      }

      const simplifiedLocation = simplifyLocation(searchLocation);

      console.log('Searching hotels with params:', {
        location: simplifiedLocation,
        checkInDate: formattedCheckIn,
        checkOutDate: formattedCheckOut,
        guests
      });

      const response = await hotelService.searchHotels(
        simplifiedLocation,
        formattedCheckIn,
        formattedCheckOut,
        guests
      );

      // Extract data from response
      let hotelsData = [];
      
      // Handle different response structures
      if (Array.isArray(response)) {
        hotelsData = response;
      } else if (response?.data) {
        hotelsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (response?.response) {
        hotelsData = Array.isArray(response.response) ? response.response : [response.response];
      } else if (response) {
        hotelsData = Array.isArray(response) ? response : [response];
      }

      console.log('Raw API response:', hotelsData);

      // Process and filter hotel data
      let validHotels = processHotelData(hotelsData);
      
      // Fetch Unsplash images for each hotel
      const hotelsWithUnsplashImages = await Promise.all(
        validHotels.map(async (hotel) => {
          try {
            const query = UnsplashService.buildHotelQuery(hotel.name, hotel.location);
            const unsplashImages = await UnsplashService.searchHotelImages(query, 1);
            
            if (unsplashImages && unsplashImages.length > 0) {
              return {
                ...hotel,
                image: unsplashImages[0].urls.regular,
                unsplashAttribution: {
                  photographer: unsplashImages[0].user.name,
                  link: unsplashImages[0].user.links.html
                }
              };
            }
          } catch (error) {
            console.warn(`Failed to fetch Unsplash image for ${hotel.name}:`, error);
          }
          return hotel;
        })
      );

      validHotels = hotelsWithUnsplashImages;
      
      console.log('Processed hotels with Unsplash images:', validHotels);
      
      if (validHotels.length === 0 && hotelsData.length > 0) {
        setError('Hotels found but data format is incomplete');
      } else {
        setHotels(validHotels);
      }
    } catch (err) {
      console.error('Error searching hotels:', err);
      setError(err.message || 'Failed to fetch hotels. Please try again.');
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBookHotel = (hotel) => {
    handleBookOnBooking(hotel, searchLocation, checkInDate, checkOutDate, guests);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <Hotel className="absolute inset-0 m-auto w-8 h-8 text-blue-500 animate-pulse" />
          </div>
          <p className="mt-4 text-lg text-gray-600">Searching for hotels...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Hotel Search</h1>
                <p className="text-emerald-100">
                  {searchLocation || 'Find your perfect stay'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-white/10 rounded-full">
                <Heart className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-full">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search Form */}
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Destination</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    placeholder="City, hotel, or landmark"
                    className="w-full p-3 text-gray-900 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
              </div>

              {/* Check-in */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in</label>
                <input
                  type="date"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  className="w-full p-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-gray-900"
                />
              </div>

              {/* Check-out */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out</label>
                <input
                  type="date"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  min={checkInDate}
                  className="w-full p-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-gray-900"
                />
              </div>
            </div>

            {/* Guests & Search Button */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mt-4 gap-4">
              <div className="relative">
                <button
                  onClick={() => setShowGuestSelector(!showGuestSelector)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <Users className="text-gray-400" size={20} />
                  <span className="text-gray-700 font-medium">
                    {guests.adults + guests.children} Guest{guests.adults + guests.children !== 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-700 font-medium">
                    {guests.rooms} Room{guests.rooms !== 1 ? 's' : ''}
                  </span>
                  <ChevronRight 
                    size={16} 
                    className={`text-gray-400 transition-transform ${showGuestSelector ? 'rotate-90' : ''}`} 
                  />
                </button>

                {/* Guest Selector Dropdown */}
                {showGuestSelector && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50">
                    {/* Adults */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <div className="font-medium text-gray-900">Adults</div>
                        <div className="text-xs text-gray-500">18+ years</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests({ ...guests, adults: Math.max(1, guests.adults - 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="w-6 text-center font-semibold text-gray-900">{guests.adults}</span>
                        <button
                          onClick={() => setGuests({ ...guests, adults: Math.min(16, guests.adults + 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Children */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <div className="font-medium text-gray-900">Children</div>
                        <div className="text-xs text-gray-500">0-17 years</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests({ ...guests, children: Math.max(0, guests.children - 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="w-6 text-center font-semibold text-gray-900">{guests.children}</span>
                        <button
                          onClick={() => setGuests({ ...guests, children: Math.min(8, guests.children + 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Rooms */}
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium text-gray-900">Rooms</div>
                        <div className="text-xs text-gray-500">Number of rooms</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests({ ...guests, rooms: Math.max(1, guests.rooms - 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          -
                        </button>
                        <span className="w-6 text-center font-semibold text-gray-900">{guests.rooms}</span>
                        <button
                          onClick={() => setGuests({ ...guests, rooms: Math.min(8, guests.rooms + 1) })}
                          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Done Button */}
                    <button
                      onClick={() => setShowGuestSelector(false)}
                      className="w-full mt-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleSearchHotels}
                disabled={loading}
                className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    Search Hotels
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        

        {/* API Data Issue Warning */}
        {hotels.length === 0 && searched && !error && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <ShieldAlert className="text-blue-500" size={20} />
            <div>
              <p className="text-blue-700 font-semibold">No complete hotel data found</p>
              <p className="text-blue-600 text-sm mt-1">
                The API returned incomplete data. Try a different search or check back later.
              </p>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searched && hotels.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {hotels.length} hotel{hotels.length !== 1 ? 's' : ''} found
            </h2>
            <p className="text-gray-600">
              Showing results for {searchLocation}
            </p>
          </div>
        )}

        {/* Hotel Grid */}
        {hotels.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {hotels.map((hotel) => (
              <HotelCard 
                key={hotel.id} 
                hotel={hotel}
                location={searchLocation}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                onBook={handleBookHotel}
              />
            ))}
          </div>
        ) : searched && !loading && (
          <div className="text-center py-12">
            <Hotel className="mx-auto w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No hotels found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or destination</p>
            <button
              onClick={handleSearchHotels}
              className="mt-4 px-6 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

       
      </div>
    </div>
  );
};

export default HotelList;